import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Swal from 'sweetalert2'
import LeadsService from '@/services/LeadsService'

export default function useLeadDetails(id) {
	const navigate = useNavigate()

	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [lead, setLead] = useState(null)

	const [deleting, setDeleting] = useState(false)

	const [newNote, setNewNote] = useState('')
	const [addingNote, setAddingNote] = useState(false)

	const fetchLead = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const { data, error } = await LeadsService.getLeadById(id)
			if (error) throw new Error(error)
			setLead(data)
		} catch (e) {
			setError(e.message || 'Failed to load lead')
		} finally {
			setLoading(false)
		}
	}, [id])

	useEffect(() => {
		if (id) fetchLead()
	}, [id, fetchLead])

	const addNote = useCallback(async () => {
		const val = newNote.trim()
		if (!val || addingNote || !lead?._id) return

		setAddingNote(true)
		setError(null)

		try {
			const payload = {
				id,
				note: val,
				added: moment().toISOString(),
			}
			const { success, error } = await LeadsService.addCommunication(payload)
			if (!success) throw new Error(error || 'Failed to add note')

			await fetchLead()
			setNewNote('')
		} catch (e) {
			setError(e.message || 'Failed to add note')
		} finally {
			setAddingNote(false)
		}
	}, [addingNote, fetchLead, id, lead?._id, newNote])

	const deleteLead = useCallback(async () => {
		if (!lead?._id) return
		const result = await Swal.fire({
			title: 'Are you sure?',
			text: `You are about to delete "${lead.customerName}". This action cannot be undone!`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			cancelButtonColor: '#3085d6',
			confirmButtonText: 'Yes, delete',
			cancelButtonText: 'Cancel',
		})
		if (!result.isConfirmed) return

		setDeleting(true)
		try {
			const { success, error } = await LeadsService.deleteLeads([lead._id])
			if (!success) throw new Error(error || 'Failed to delete lead')

			const msg = `Lead "${lead.customerName}" has been deleted successfully.`
			await Swal.fire({ title: 'Deleted!', text: msg, icon: 'success' })
			navigate('/leads', { state: { message: msg } })
		} catch (e) {
			await Swal.fire({ title: 'Error!', text: e.message || 'Unexpected error while deleting.', icon: 'error' })
		} finally {
			setDeleting(false)
		}
	}, [lead, navigate])

	return {
		lead,
		loading,
		error,

		fetchLead,
		deleteLead,

		newNote,
		setNewNote,
		addingNote,
		addNote,

		deleting,
		setLead,
		setError,
	}
}
